// exportUtils.js — all export logic, no React dependencies

const DIVIDER = '══════════════════════════════';
const SECTION_LEN = 28;

// ── Helpers ──────────────────────────────────────────────────────────────────

export function stripHtml(html) {
  if (!html) return '';
  let text = html
    .replace(/<li[^>]*>/gi, '\n• ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n');
  try {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    text = doc.body.innerText || doc.body.textContent || '';
  } catch {
    text = text.replace(/<[^>]+>/g, '');
  }
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

export function starsFromRating(rating) {
  const n = parseInt(rating, 10);
  if (!n || n < 1) return '';
  const filled = Math.min(n, 5);
  return '★'.repeat(filled) + '☆'.repeat(5 - filled);
}

export function formatExportDate(date) {
  if (!date) return '';
  let d;
  if (date instanceof Date) d = date;
  else if (typeof date.toDate === 'function') d = date.toDate(); // Firestore Timestamp
  else if (date.seconds != null) d = new Date(date.seconds * 1000); // plain Timestamp shape
  else d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function slugify(str) {
  return (str || 'export')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50) || 'export';
}

function sect(label, content) {
  if (!content || !String(content).trim()) return '';
  const dashes = '─'.repeat(Math.max(2, SECTION_LEN - label.length));
  return `\n── ${label} ${dashes}\n${String(content).trim()}\n`;
}

function computeSessionMinutes(session) {
  const segs = Array.isArray(session?.segments) ? session.segments : [];
  if (!segs.length) {
    const s = session?.startedAt, e = session?.endedAt;
    if (!s || !e) return 0;
    const sm = s instanceof Date ? s.getTime() : new Date(s).getTime();
    const em = e instanceof Date ? e.getTime() : new Date(e).getTime();
    return Math.max(0, Math.floor((em - sm) / 60000));
  }
  let totalMs = 0;
  const now = Date.now();
  segs.forEach(seg => {
    const startTs = seg.startedAt instanceof Date ? seg.startedAt.getTime() : new Date(seg.startedAt).getTime();
    const endRaw = seg.endedAt;
    const endTs = endRaw ? (endRaw instanceof Date ? endRaw.getTime() : new Date(endRaw).getTime()) : now;
    if (!isNaN(startTs) && !isNaN(endTs) && endTs > startTs) totalMs += (endTs - startTs);
  });
  return Math.max(0, Math.floor(totalMs / 60000));
}

function formatDuration(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return [h > 0 ? `${h} hr${h > 1 ? 's' : ''}` : null, m > 0 ? `${m} min` : null].filter(Boolean).join(', ') || '0 min';
}

function dateFooter(obj) {
  return [
    ['Added',    obj?.createdAt],
    ['Modified', obj?.updatedAt],
    ['Viewed',   obj?.lastViewedAt],
  ].map(([label, val]) => {
    const s = formatExportDate(val);
    return s ? `${label}: ${s}` : null;
  }).filter(Boolean);
}

function collectLinks(tune) {
  return [
    ...(tune.links || []),
    tune.link1, tune.link2, tune.link3,
  ].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
}

// ── Plain Text Builders ───────────────────────────────────────────────────────

export function tunePlainText(tune) {
  if (!tune) return '';
  const isNote = tune.mode === 'notes';
  const label = isNote ? 'NOTE' : 'TUNE';
  const lines = [];

  lines.push(DIVIDER);
  lines.push(`${label}: ${(tune.title || 'Untitled').toUpperCase()}`);
  lines.push(DIVIDER);

  const meta = [];
  if (!isNote && tune.type) meta.push(`Type: ${tune.type}`);
  if (!isNote && tune.key) meta.push(`Key: ${tune.key}`);
  if (tune.genre) meta.push(`Genre: ${tune.genre}`);
  if (tune.status) meta.push(`Status: ${tune.status}`);
  const stars = starsFromRating(tune.rating);
  if (stars) meta.push(`Rating: ${stars}`);
  if (meta.length) lines.push(meta.join('  |  '));
  if (tune.customTags?.length) lines.push(`Tags: ${tune.customTags.join(', ')}`);
  if (!isNote && tune.composer) lines.push(`Composer: ${tune.composer}`);

  if (tune.quickNote) lines.push(sect('Quick Note', tune.quickNote));
  const notesText = stripHtml(tune.notes);
  if (notesText) lines.push(sect('Notes', notesText));
  if (!isNote && tune.lyrics) lines.push(sect('Lyrics', tune.lyrics));
  if (!isNote && tune.abc) lines.push(sect('ABC Notation', tune.abc));

  const links = collectLinks(tune);
  if (links.length) lines.push(sect('Links', links.join('\n')));

  if (tune.recordings?.length) {
    const labels = tune.recordings.map(r => r.label || 'Recording').join(' | ');
    lines.push(sect('Recordings', labels));
  }
  if (tune.attachments?.length) {
    lines.push(sect('Attachments', tune.attachments.map(a => a.name).join(', ')));
  }

  const dateParts = dateFooter(tune);
  if (dateParts.length) lines.push('\n' + dateParts.join('  |  '));

  return lines.join('\n');
}

export function setPlainText(set, resolvedTunes, { fullDetails = true } = {}) {
  if (!set) return '';
  const isFolder = set.mode === 'notes';
  const label = isFolder ? 'FOLDER' : 'SET';
  const lines = [];

  lines.push(DIVIDER);
  lines.push(`${label}: ${(set.title || 'Untitled').toUpperCase()}`);
  lines.push(DIVIDER);

  const meta = [];
  if (set.genre) meta.push(`Genre: ${set.genre}`);
  const stars = starsFromRating(set.rating);
  if (stars) meta.push(`Rating: ${stars}`);
  if (set.status) meta.push(`Status: ${set.status}`);
  if (meta.length) lines.push(meta.join('  |  '));
  if (set.customTags?.length) lines.push(`Tags: ${set.customTags.join(', ')}`);

  const notesText = stripHtml(set.notes);
  if (notesText) lines.push(sect('Notes', notesText));

  const tuneList = resolvedTunes.map((t, i) => {
    const sub = [t.type, t.key].filter(Boolean).join(', ');
    return ` ${String(i + 1).padStart(2)}. ${t.title || 'Untitled'}${sub ? `  (${sub})` : ''}`;
  });
  if (tuneList.length) lines.push(sect(isFolder ? 'Contents' : 'Tunes', tuneList.join('\n')));

  const setDates = dateFooter(set);
  if (setDates.length) lines.push('\n' + setDates.join('  |  '));

  if (fullDetails && resolvedTunes.length) {
    const hr = '─'.repeat(30);
    lines.push(`\n\n${hr} Full Details ${hr}`);
    lines.push(resolvedTunes.map(t => '\n' + tunePlainText(t)).join(`\n\n${DIVIDER}\n`));
  }

  return lines.join('\n');
}

export function sessionPlainText(session, { fullDetails = true } = {}) {
  if (!session) return '';
  const lines = [];

  lines.push(DIVIDER);
  lines.push(`SESSION: ${(session.location || 'Session').toUpperCase()}`);
  lines.push(DIVIDER);

  const date = session.startedAt ? formatExportDate(session.startedAt) : '';
  const totalMinutes = computeSessionMinutes(session);
  const dur = totalMinutes > 0 ? formatDuration(totalMinutes) : '';
  const meta = [date, dur].filter(Boolean);
  if (meta.length) lines.push(meta.join('  |  '));
  if (session.customTags?.length) lines.push(`Tags: ${session.customTags.join(', ')}`);

  const notesText = stripHtml(session.notes);
  if (notesText) lines.push(sect('Notes', notesText));

  const tunes = session.tunes || [];
  const tuneList = tunes.map((t, i) => {
    const sub = [t.type, t.key].filter(Boolean).join(', ');
    return ` ${String(i + 1).padStart(2)}. ${t.title || 'Untitled'}${sub ? `  (${sub})` : ''}`;
  });
  if (tuneList.length) lines.push(sect('Tunes Played', tuneList.join('\n')));

  const sessionDates = dateFooter(session);
  if (sessionDates.length) lines.push('\n' + sessionDates.join('  |  '));

  if (fullDetails && tunes.length) {
    const hr = '─'.repeat(30);
    lines.push(`\n\n${hr} Full Details ${hr}`);
    lines.push(tunes.map(t => '\n' + tunePlainText(t)).join(`\n\n${DIVIDER}\n`));
  }

  return lines.join('\n');
}

export function bulkPlainText(tunes) {
  if (!tunes?.length) return '';
  return tunes.map(t => tunePlainText(t)).join(`\n\n${DIVIDER}\n\n`);
}

// ── Print HTML Builders ───────────────────────────────────────────────────────

const PRINT_CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  body { font-family: Georgia, 'Times New Roman', serif; color: #222; background: #fff; margin: 0; padding: 0; font-size: 16px; }
  h1 { font-size: 1.7rem; margin: 0 0 0.3rem; line-height: 1.2; }
  h2 { font-size: 1.05rem; font-weight: 700; margin: 1.4rem 0 0.35rem; border-bottom: 1px solid #ccc; padding-bottom: 0.15rem; letter-spacing: 0.02em; text-transform: uppercase; color: #444; }
  p { margin: 0.2rem 0; line-height: 1.6; }
  a { color: #1c7ed6; }
  .meta { color: #555; font-size: 0.9rem; }
  .tags { color: #888; font-size: 0.82rem; margin: 0.15rem 0; }
  .tune-page, .cover-page { padding: 2rem 2.5rem; }
  .tune-list { list-style: none; padding: 0; margin: 0; counter-reset: tune-counter; }
  .tune-list li { counter-increment: tune-counter; padding: 0.3rem 0 0.3rem 2rem; border-bottom: 1px solid #eee; font-size: 0.95rem; position: relative; }
  .tune-list li::before { content: counter(tune-counter) "."; position: absolute; left: 0; color: #888; }
  .meta-small { color: #777; font-size: 0.85rem; margin-left: 0.4rem; }
  .abc-svg { overflow: visible; padding-bottom: 2rem; }
  .abc-svg svg { max-width: 100%; display: block; overflow: visible; }
  .abc-svg .abcjs-title { display: none; }
  pre.abc-text { font-family: 'Courier New', monospace; font-size: 0.75rem; background: #f7f7f7; padding: 0.75rem 1rem; border-radius: 4px; white-space: pre-wrap; word-break: break-all; border: 1px solid #e5e5e5; }
  pre.lyrics { font-family: inherit; font-size: 0.9rem; white-space: pre-wrap; margin: 0; }
  .notes-content { font-size: 0.92rem; line-height: 1.65; }
  .notes-content ul, .notes-content ol { padding-left: 1.4rem; margin: 0.3rem 0; }
  .page-break { page-break-before: always; break-before: page; }
  .sub-label { font-size: 0.82rem; color: #999; font-style: italic; margin: 0 0 0.5rem; }
  .close-btn { position: fixed; top: 1rem; right: 1rem; background: #333; color: #fff; border: none; border-radius: 6px; padding: 0.5rem 1rem; font-size: 0.9rem; cursor: pointer; z-index: 9999; }
  @media print { .close-btn { display: none; } @page { margin: 0.75in; } }
`;

function htmlDoc(title, bodyContent) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escHtml(title)}</title>
  <style>${PRINT_CSS}</style>
</head>
<body>
<button class="close-btn" onclick="window.close()">✕ Close</button>
${bodyContent}
<script>window.onload = function() { setTimeout(function(){ window.print(); }, 350); };<\/script>
</body>
</html>`;
}

function escHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function tuneHtmlBody(tune, svgString) {
  if (!tune) return '';
  const isNote = tune.mode === 'notes';
  const stars = starsFromRating(tune.rating);
  const metaParts = [
    !isNote && tune.type,
    !isNote && tune.key && `Key of ${tune.key}`,
    tune.genre,
    tune.status,
    stars,
  ].filter(Boolean);
  const links = collectLinks(tune);

  let html = `<div class="tune-page">
  <h1>${escHtml(tune.title || 'Untitled')}</h1>
  <p class="sub-label">${isNote ? 'Note' : 'Tune'}</p>
  ${metaParts.length ? `<p class="meta">${escHtml(metaParts.join('  ·  '))}</p>` : ''}
  ${tune.customTags?.length ? `<p class="tags">${escHtml(tune.customTags.map(t => '#' + t).join(' '))}</p>` : ''}
  ${!isNote && tune.composer ? `<p class="meta">Composer: ${escHtml(tune.composer)}</p>` : ''}`;

  if (tune.quickNote) {
    html += `<h2>Quick Note</h2><p>${escHtml(tune.quickNote)}</p>`;
  }
  if (tune.notes) {
    html += `<h2>Notes</h2><div class="notes-content">${tune.notes}</div>`;
  }
  if (!isNote && tune.lyrics) {
    html += `<h2>Lyrics</h2><pre class="lyrics">${escHtml(tune.lyrics)}</pre>`;
  }
  if (!isNote && tune.abc) {
    html += `<h2>ABC Notation</h2>`;
    html += svgString
      ? `<div class="abc-svg">${svgString}</div>`
      : `<pre class="abc-text">${escHtml(tune.abc)}</pre>`;
  }
  if (links.length) {
    html += `<h2>Links</h2><ul style="padding-left:1.2rem;margin:0">` +
      links.map(l => `<li><a href="${escHtml(l)}">${escHtml(l)}</a></li>`).join('') +
      `</ul>`;
  }

  const dateParts = dateFooter(tune);
  if (dateParts.length) {
    html += `<p class="meta" style="margin-top:2rem;font-size:0.78rem;color:#bbb">${escHtml(dateParts.join('  |  '))}</p>`;
  }
  html += `</div>`;
  return html;
}

export function tunePrintHtml(tune, svgString) {
  return htmlDoc(tune?.title || 'Tune', tuneHtmlBody(tune, svgString));
}

export function setPrintHtml(set, resolvedTunes, svgMap, { fullDetails = true } = {}) {
  const isFolder = set?.mode === 'notes';
  const label = isFolder ? 'Folder' : 'Set';
  const stars = starsFromRating(set?.rating);
  const metaParts = [set?.genre, stars].filter(Boolean);

  let body = `<div class="cover-page">
  <h1>${escHtml(set?.title || 'Untitled')}</h1>
  <p class="sub-label">${label}${set?.createdAt ? `  ·  ${formatExportDate(set.createdAt)}` : ''}</p>
  ${metaParts.length ? `<p class="meta">${escHtml(metaParts.join('  ·  '))}</p>` : ''}
  ${set?.customTags?.length ? `<p class="tags">${escHtml(set.customTags.map(t => '#' + t).join(' '))}</p>` : ''}
  ${set?.notes ? `<div class="notes-content" style="margin-top:0.8rem">${set.notes}</div>` : ''}
  <h2>${isFolder ? 'Contents' : 'Tunes'}</h2>
  <ol class="tune-list">
    ${resolvedTunes.map(t => {
      const sub = [t.type, t.key].filter(Boolean).join(', ');
      return `<li>${escHtml(t.title || 'Untitled')}${sub ? `<span class="meta-small">(${escHtml(sub)})</span>` : ''}</li>`;
    }).join('\n    ')}
  </ol>
  ${(() => { const d = dateFooter(set); return d.length ? `<p class="meta" style="margin-top:1.5rem;font-size:0.78rem;color:#bbb">${escHtml(d.join('  |  '))}</p>` : ''; })()}
</div>`;

  if (fullDetails && resolvedTunes.length) {
    body += resolvedTunes.map(t =>
      `<div class="page-break">${tuneHtmlBody(t, svgMap?.[t.id] || null)}</div>`
    ).join('\n');
  }
  return htmlDoc(set?.title || label, body);
}

export function sessionPrintHtml(session, svgMap, { fullDetails = true } = {}) {
  const totalMinutes = computeSessionMinutes(session);
  const dur = totalMinutes > 0 ? formatDuration(totalMinutes) : '';
  const date = session?.startedAt ? formatExportDate(session.startedAt) : '';
  const metaParts = [date, dur].filter(Boolean);
  const tunes = session?.tunes || [];

  let body = `<div class="cover-page">
  <h1>${escHtml(session?.location || 'Session')}</h1>
  <p class="sub-label">Session</p>
  ${metaParts.length ? `<p class="meta">${escHtml(metaParts.join('  ·  '))}</p>` : ''}
  ${session?.customTags?.length ? `<p class="tags">${escHtml(session.customTags.map(t => '#' + t).join(' '))}</p>` : ''}
  ${session?.notes ? `<div class="notes-content" style="margin-top:0.8rem">${session.notes}</div>` : ''}
  <h2>Tunes Played</h2>
  <ol class="tune-list">
    ${tunes.map(t => {
      const sub = [t.type, t.key].filter(Boolean).join(', ');
      return `<li>${escHtml(t.title || 'Untitled')}${sub ? `<span class="meta-small">(${escHtml(sub)})</span>` : ''}</li>`;
    }).join('\n    ')}
  </ol>
  ${(() => { const d = dateFooter(session); return d.length ? `<p class="meta" style="margin-top:1.5rem;font-size:0.78rem;color:#bbb">${escHtml(d.join('  |  '))}</p>` : ''; })()}
</div>`;

  if (fullDetails && tunes.length) {
    body += tunes.map(t =>
      `<div class="page-break">${tuneHtmlBody(t, svgMap?.[t.id] || null)}</div>`
    ).join('\n');
  }
  return htmlDoc(session?.location || 'Session', body);
}

export function bulkPrintHtml(tunes, svgMap) {
  if (!tunes?.length) return htmlDoc('Export', '<p>Nothing to export.</p>');
  const body = tunes.map((t, i) =>
    `${i > 0 ? '<div class="page-break">' : '<div>'}${tuneHtmlBody(t, svgMap?.[t.id] || null)}</div>`
  ).join('\n');
  return htmlDoc('AllMyTunes Export', body);
}

// ── Markdown Archive ──────────────────────────────────────────────────────────

// Preserve title as filename (spaces, parens etc. kept) — only strip chars illegal on filesystems.
function safeFilename(title) {
  return (title || 'untitled')
    .replace(/[/\\:*?"<>|]/g, '-')
    .replace(/\.+$/, '')
    .trim() || 'untitled';
}

function yamlStr(val) {
  if (!val) return '';
  const s = String(val);
  // Quote if contains special YAML chars
  return /[:#\[\]{},|>&*!%@`'"]/.test(s) || s.includes('\n') ? `"${s.replace(/"/g, '\\"')}"` : s;
}

function isoToDate(val) {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val.seconds != null) return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

function isoStr(val) {
  const d = isoToDate(val);
  return d ? d.toISOString().slice(0, 10) : '';
}

// Convert <span data-amn-link="1" ...>Title</span> to [[Title]] and collect link refs.
function convertInternalLinks(html) {
  const linked = [];
  const converted = (html || '').replace(
    /<span\b([^>]*data-amn-link="1"[^>]*)>([\s\S]*?)<\/span>/gi,
    (_match, attrs, inner) => {
      const idM   = attrs.match(/data-amn-link-id="([^"]+)"/i);
      const typeM = attrs.match(/data-amn-link-type="([^"]+)"/i);
      const linkTitle = inner.replace(/<[^>]*>/g, '').trim();
      if (idM?.[1]) linked.push({ id: idM[1], type: typeM?.[1] || 'note', title: linkTitle });
      return linkTitle ? `[[${linkTitle}]]` : '';
    }
  );
  return { converted, linked };
}

// backlinks: [{ title, id }] — notes that link TO this note (computed by buildArchiveZip)
// urlToLocalPath: Map<firebaseUrl, 'files/filename.ext'> for bundled attachments
export function tuneToMarkdown(tune, { backlinks = [], urlToLocalPath = {} } = {}) {
  if (!tune) return '';
  const isNote = tune.mode === 'notes';

  const { converted: notesHtml } = convertInternalLinks(tune.notes || '');

  const lines = ['---'];
  lines.push(`title: ${yamlStr(tune.title || 'Untitled')}`);
  lines.push(`mode: ${tune.mode || 'tunes'}`);
  if (tune.type)    lines.push(`type: ${yamlStr(tune.type)}`);
  if (!isNote && tune.key) lines.push(`key: ${yamlStr(tune.key)}`);
  if (tune.genre)   lines.push(`${isNote ? 'category' : 'genre'}: ${yamlStr(tune.genre)}`);
  if (tune.status)  lines.push(`status: ${yamlStr(tune.status)}`);
  if (tune.rating)  lines.push(`rating: ${tune.rating}`);
  if (!isNote && tune.composer) lines.push(`composer: ${yamlStr(tune.composer)}`);
  if (tune.customTags?.length)  lines.push(`tags: [${tune.customTags.map(yamlStr).join(', ')}]`);
  const created = isoStr(tune.createdAt);
  const updated = isoStr(tune.updatedAt);
  if (created) lines.push(`created: ${created}`);
  if (updated) lines.push(`updated: ${updated}`);
  lines.push(`id: ${tune.id || ''}`);
  lines.push('---');
  lines.push('');

  const title = tune.title || 'Untitled';
  lines.push(`# ${title}`);
  lines.push('');

  if (tune.quickNote) {
    lines.push(`> ${tune.quickNote}`);
    lines.push('');
  }

  const bodyText = stripHtml(notesHtml);
  if (bodyText) {
    lines.push(bodyText);
    lines.push('');
  }

  if (!isNote && tune.lyrics) {
    lines.push('## Lyrics');
    lines.push('');
    lines.push(tune.lyrics);
    lines.push('');
  }

  if (!isNote && tune.abc) {
    lines.push('## ABC Notation');
    lines.push('');
    lines.push('```abc');
    lines.push(tune.abc.trim());
    lines.push('```');
    lines.push('');
  }

  const links = collectLinks(tune);
  if (links.length) {
    lines.push('## Links');
    lines.push('');
    links.forEach(l => lines.push(`- ${l}`));
    lines.push('');
  }

  if (tune.attachments?.length) {
    lines.push('## Attachments');
    lines.push('');
    tune.attachments.forEach(a => {
      const local = a.url && urlToLocalPath[a.url];
      if (local) {
        lines.push(`- ![[${local}]]`);
      } else if (a.url) {
        lines.push(`- [${a.name || 'attachment'}](${a.url})`);
      } else {
        lines.push(`- ${a.name || 'attachment'}`);
      }
    });
    lines.push('');
  }

  if (tune.recordings?.length) {
    lines.push('## Recordings');
    lines.push('');
    tune.recordings.forEach(r => {
      const label = r.label || r.name || 'Recording';
      const local = r.url && urlToLocalPath[r.url];
      if (local) {
        lines.push(`- ![[${local}]]`);
      } else if (r.url) {
        lines.push(`- [${label}](${r.url})`);
      } else {
        lines.push(`- ${label}`);
      }
    });
    lines.push('');
  }

  if (backlinks.length) {
    lines.push('## Backlinks');
    lines.push('');
    backlinks.forEach(l => lines.push(`- [[${l.title}]]`));
    lines.push('');
  }

  return lines.join('\n');
}

// Build a ZIP archive (Uint8Array) containing one .md per tune/note plus database.json.
// Requires fflate: import { zipSync, strToU8 } from 'fflate'
export async function buildArchiveZip(tunes, { includeFiles = true } = {}) {
  const { zipSync, strToU8 } = await import('fflate');

  // Build backlinks map: targetId → [{ title, id }] of tunes that link to it
  const backlinksMap = {};
  for (const tune of tunes) {
    if (!tune.notes) continue;
    const { linked } = convertInternalLinks(tune.notes);
    for (const link of linked) {
      if (!backlinksMap[link.id]) backlinksMap[link.id] = [];
      backlinksMap[link.id].push({ title: tune.title || 'Untitled', id: tune.id });
    }
  }

  const urlToLocalPath = {};
  const files = {};

  if (includeFiles) {
    const usedFileNames = new Set();
    for (const tune of tunes) {
      const allFiles = [
        ...(tune.attachments || []).map(a => ({ name: a.name, url: a.url })),
        ...(tune.recordings || []).map(r => ({ name: r.label || r.name || 'recording', url: r.url })),
      ];
      for (const f of allFiles) {
        if (!f.url || urlToLocalPath[f.url]) continue; // skip missing or already-seen URLs
        const rawName = (f.name || 'file').replace(/[/\\?%*:|"<>]/g, '-');
        let localPath = `AllMyTunes/files/${rawName}`;
        if (usedFileNames.has(localPath)) {
          const dot = rawName.lastIndexOf('.');
          const [base, ext] = dot > 0 ? [rawName.slice(0, dot), rawName.slice(dot)] : [rawName, ''];
          const shortId = (tune.id || '').slice(-6) || String(Date.now()).slice(-6);
          localPath = `AllMyTunes/files/${base}-${shortId}${ext}`;
        }
        usedFileNames.add(localPath);
        try {
          const resp = await fetch(f.url);
          if (resp.ok) {
            const buf = await resp.arrayBuffer();
            files[localPath] = [new Uint8Array(buf), { level: 0 }];
            // store vault-relative path (without AllMyTunes/ prefix) for wikilinks
            urlToLocalPath[f.url] = localPath.replace('AllMyTunes/', '');
          }
        } catch {
          // URL unreachable — markdown link will use original Firebase URL
        }
      }
    }
  }

  const usedFilenames = new Set();

  for (const tune of tunes) {
    const isNote = tune.mode === 'notes';
    const folder = isNote ? 'notes' : 'tunes';
    const base = safeFilename(tune.title || (isNote ? 'Untitled Note' : 'Untitled Tune'));
    let filename = `AllMyTunes/${folder}/${base}.md`;
    // Deduplicate
    if (usedFilenames.has(filename)) {
      const shortId = (tune.id || '').slice(-6) || String(Date.now()).slice(-6);
      filename = `AllMyTunes/${folder}/${base}-${shortId}.md`;
    }
    usedFilenames.add(filename);
    const backlinks = backlinksMap[tune.id] || [];
    files[filename] = [strToU8(tuneToMarkdown(tune, { backlinks, urlToLocalPath })), { level: 6 }];
  }

  const db = {
    exportedAt: new Date().toISOString(),
    count: tunes.length,
    tunes: tunes.map(t => {
      // Serialize Firestore Timestamps to ISO strings for JSON
      const out = { ...t };
      for (const k of ['createdAt', 'updatedAt', 'lastViewedAt']) {
        if (out[k]) out[k] = isoStr(out[k]) || out[k];
      }
      return out;
    }),
  };
  files['database.json'] = [strToU8(JSON.stringify(db, null, 2)), { level: 6 }];

  return zipSync(files);
}

// ── File Triggers ─────────────────────────────────────────────────────────────

export function downloadZip(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export async function shareOrDownloadText(content, filename) {
  const file = new File([content], filename, { type: 'text/plain' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename });
      return;
    } catch (err) {
      if (err.name === 'AbortError') return; // user cancelled — don't fall through
    }
  }
  // Fallback: trigger download
  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export function openPrintWindow(html) {
  const win = window.open('', '_blank');
  if (!win) {
    alert('Please allow pop-ups to use the Print / PDF feature.');
    return;
  }
  win.document.write(html);
  win.document.close();
}
